// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { CommonModule } from '@angular/common';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  computed,
  inject,
  input,
} from '@angular/core';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { RouteService } from 'src/app/modules/portal/services/routeService.service';
import { LocalizedDatePipe } from 'src/app/modules/shared/pipes/localizedDate.pipe';
import { MrqColumn, MrqReportData } from '../../interfaces/mrq.interfaces';
import { MrqReportService } from '../../services/mrq-report/mrq-report.service';
import {
  capitalizeHeader,
  formatNumber,
} from '../../utils/mrq-table-formatting';

interface SystemParams {
  systemMaterialNumber: string | number;
  systemSerialNumber: string | number;
  [key: string]: unknown;
}

@Component({
  selector: 'app-mrq-mobile-table',
  templateUrl: './mrq-mobile-table.component.html',
  styleUrls: ['./mrq-mobile-table.component.scss'],
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [CommonModule, MatTableModule, MatTooltipModule, LocalizedDatePipe],
})
export class MrqMobileTableComponent {
  private router = inject(Router);
  private routeService = inject(RouteService);
  private mrqReportService = inject(MrqReportService);

  reportData = input.required<MrqReportData[]>();
  columns = input.required<MrqColumn[]>();
  magnetType = input.required<string>();

  mostRecentDateData = computed(() =>
    this.mrqReportService.getMostRecentDateData(this.reportData()),
  );

  tableDataSource = computed(() => {
    const recentData = this.mrqReportService.getMostRecentDateData(
      this.reportData(),
    );
    if (!recentData) {
      return [];
    }
    return this.mrqReportService
      .getMobileColumns(this.columns())
      .map((column) => {
        const rawValue = recentData.values[column.id];
        const formattedValue =
          typeof rawValue === 'number' ? formatNumber(rawValue) : rawValue;
        return {
          id: column.id,
          name: capitalizeHeader(column.name || column.id, this.magnetType()),
          value: formattedValue,
          tooltip: column.tooltip,
        };
      });
  });

  // Column definitions for mat-table
  displayedColumns: string[] = ['name', 'value', 'icon'];

  emptyValueCheck(value: unknown): string | number {
    if (!value) {
      return '-';
    }
    if (typeof value === 'number' || typeof value === 'string') {
      return value;
    }
    return '-';
  }

  onRowClick(element: {
    id: string;
    name: string;
    value: string | number;
    tooltip?: string;
  }): void {
    this.navigateToDetails(element);
  }

  private navigateToDetails(element: {
    id: string;
    name: string;
    value: string | number;
    tooltip?: string;
  }): void {
    const params = this.routeService.params as SystemParams;
    if (!this.hasValidSystemParams(params)) {
      return;
    }
    this.router.navigate(this.buildSystemDetailsRoute(params), {
      queryParams: this.buildQueryParams(element),
    });
  }

  private hasValidSystemParams(params: SystemParams): boolean {
    return !!(params.systemMaterialNumber && params.systemSerialNumber);
  }

  private buildSystemDetailsRoute(params: SystemParams): string[] {
    return [
      '/system',
      params.systemMaterialNumber.toString(),
      params.systemSerialNumber.toString(),
      'details',
    ];
  }

  private buildQueryParams(element: {
    id: string;
    name: string;
    value: string | number;
    tooltip?: string;
  }) {
    return {
      id: element.id,
      name: element.name,
      magnetType: this.magnetType(),
    };
  }
}
